import * as React from 'react';
import { Form, Formik } from 'formik';
import Box from '@mui/material/Box';
import { LoadingButton } from '@mui/lab';
import { Stack, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import TextareaInput from '../../form/base/TextareaInput';
import FileInput from '../../form/base/FileInput';
import {
  GetWaiversData,
  StaticLiabilityWaiverValues,
  staticWaiverSettingsSchema,
} from '../../../types/waivers';

import '../../form/form.css';
import useUpdateWaiver from '../../../hooks/waivers/useUpdateWaiver';
import { uploadWaiverDocument } from '../../../services/waivers';

interface Props {
  onSuccess?: () => void;
  draft: GetWaiversData | null | undefined;
}

export default function StaticLiabilityWaiverForm({ onSuccess, draft }: Props) {
  const [formError, setFormError] = React.useState('');

  const { mutation, isPending } = useUpdateWaiver({ draft });

  const [initialFormState] = React.useState<StaticLiabilityWaiverValues>({
    document: draft?.staticWaiverSettings?.documentLink || null,
    attestationText: draft?.staticWaiverSettings?.attestationText || '',
  });

  const onSubmit = async (values: StaticLiabilityWaiverValues) => {
    if (!draft?.businessId || isPending) return;
    try {
      setFormError('');

      const staticWaiverSettings: Parameters<
        typeof mutation.mutateAsync
      >[0]['staticWaiverSettings'] = {
        attestationText: values.attestationText,
      };

      const url =
        values.document instanceof File
          ? await uploadWaiverDocument({
              businessId: draft.businessId,
              name: draft.name,
              file: values.document,
              type: 'static',
            })
          : values.document;

      if (values.document instanceof File && !url) {
        setFormError('Failed to upload document');
        return;
      }

      staticWaiverSettings.documentLink = url;

      const response = await mutation.mutateAsync({
        staticWaiverSettings,
      });

      if (response) {
        toast.success('Static waiver settings saved successfully');
        onSuccess?.();
      }
    } catch {
      toast.error('Failed to update static waiver settings');
    }
  };

  return (
    <Box>
      <Formik
        enableReinitialize
        initialValues={initialFormState}
        validationSchema={staticWaiverSettingsSchema}
        onSubmit={onSubmit}>
        {({ isSubmitting, isValid, isValidating, dirty }) => (
          <Form>
            {formError && (
              <Typography variant="body1" color="primary">
                {formError}
              </Typography>
            )}
            <Stack width="100%" spacing={2.5}>
              <FileInput
                label="Upload document"
                name="document"
                accept={['.pdf']}
                acceptLabel="PDF"
                disabled={isSubmitting}
              />

              <TextareaInput
                tooltip="The attestation text will be added just below your waiver document, right above where the client will sign the document."
                label="Attestation text"
                name="attestationText"
                placeholder="Enter attestation text here"
                disabled={isSubmitting}
              />
            </Stack>

            <LoadingButton
              type="submit"
              disabled={
                !isValid || isSubmitting || !dirty || isValidating || isPending
              }
              variant="contained"
              className="primary"
              loading={isSubmitting}>
              <span>Save</span>
            </LoadingButton>
          </Form>
        )}
      </Formik>
    </Box>
  );
}
